import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Action, AgendaCandidate, Meeting, MeetingSummary, ProposedAgendaItem } from '@syndiqia/governance/contracts';
import { useApi } from './api';
import { useSyndicate } from './session';

export interface NoticeApproval {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  decidedAt: string | null;
  createdAt: string;
  payload: {
    meetingId: string;
    items: { id: string; name: string; email: string; language: 'fr' | 'en'; subject: string; text: string }[];
    unreachable: { name: string }[];
  };
  decision: { outcome: string; excludedItemIds?: string[]; result?: { sent: number } } | null;
}

export type AgendaDraft = { candidates: AgendaCandidate[]; proposals: ProposedAgendaItem[] };

function useGovernanceQuery<T>(key: unknown[], path: string, enabled = true) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'governance', ...key], queryFn: () => api<T>(`${s.api}/governance${path}`), enabled: enabled && s.can('governance.read') });
}

export const useMeetings = () => useGovernanceQuery<MeetingSummary[]>(['meetings'], '/meetings');
export const useMeeting = (id: string) => useGovernanceQuery<Meeting>(['meeting', id], `/meetings/${id}`);
export const useOpenActions = () => useGovernanceQuery<Action[]>(['actions', 'open'], '/actions?status=open');
export function useNotice(meetingId: string) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'governance', 'notice', meetingId],
    queryFn: async () => (await api<{ notice: NoticeApproval | null }>(`${s.api}/governance/meetings/${meetingId}/notice`)).notice,
    enabled: s.can('governance.read'),
  });
}

/** A write to the Governance API (or the approval queue); refreshes governance, approvals and documents. */
export function useGovernanceMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  const api = useApi();
  const s = useSyndicate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Input) => request(api, s.api, input),
    onSuccess: () =>
      Promise.all(['governance', 'approvals', 'documents', 'comms'].map((key) => queryClient.invalidateQueries({ queryKey: [s.tenantId, key] }))),
  });
}

/** Date and time of a meeting, in Quebec time. */
export function meetingWhen(iso: string, locale: 'fr' | 'en'): string {
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { dateStyle: 'full', timeStyle: 'short', timeZone: 'America/Toronto' }).format(new Date(iso));
}
